import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { makeMixboxItem, tierUnitPrice } from "@/lib/pricing";

// Rebuilds a past order's items from the live catalogue (current prices) and drops them in the basket.
export const rebuildOrderItems = (order, { flavors, sizes, multipacks }) => {
  const fl = new Map(flavors.map((f) => [f.id, f]));
  const sz = new Map(sizes.map((s) => [s.id, s]));
  const mp = new Map(multipacks.map((m) => [m.id, m]));
  const items = [];
  const skipped = [];
  for (const it of order.items || []) {
    if (it.type === "single" || it.type === "offer") {
      const f = fl.get(it.flavor_id), s = sz.get(it.size_id);
      if (!f || !s) { skipped.push(it.flavor_name || "item"); continue; }
      const type = it.type;
      items.push({
        key: type === "offer" ? `offer-${f.id}-${it.base}` : `single-${f.id}-${it.base}-${s.id}`, type,
        flavor_id: f.id, flavor_name: f.name, accent_color: f.accent_color, base: it.base, size_id: s.id, size_label: s.label,
        quantity: it.quantity, unit_price: tierUnitPrice(s.tiers, it.quantity), weight_g: s.weight_g,
      });
    } else if (it.type === "mixbox") {
      const s = sz.get(it.size_id);
      if (!s) { skipped.push(`${it.size_label || ""} box`); continue; }
      const sels = (it.selections || []).filter((x) => fl.has(x.flavor_id)).map((x) => ({ flavor_id: x.flavor_id, flavor_name: fl.get(x.flavor_id).name, base: x.base, boost: x.boost || "standard", quantity: x.quantity }));
      if (!sels.length) { skipped.push(`${s.label} box`); continue; }
      items.push(makeMixboxItem(s, sels, { tiers: s.tiers, accentColor: fl.get(sels[0].flavor_id)?.accent_color }));
    } else if (it.type === "multipack") {
      const m = mp.get(it.multipack_id), s = m && sz.get(m.size_id);
      const sels = (it.selections || []).filter((x) => fl.has(x.flavor_id)).map((x) => ({ ...x, flavor_name: fl.get(x.flavor_id).name }));
      if (!m || !s || m.active === false || sels.reduce((a, x) => a + x.quantity, 0) !== m.bottle_count) { skipped.push(it.multipack_name || "multipack"); continue; }
      items.push({ key: `mp-${m.id}-${Date.now()}`, type: "multipack", multipack_id: m.id, name: m.name, size_id: m.size_id, size_label: s.label,
        bottle_count: m.bottle_count, selections: sels, quantity: it.quantity, unit_price: m.fixed_price, weight_g: s.weight_g * m.bottle_count });
    } else if (it.type === "guest") {
      const f = fl.get(it.flavor_id);
      if (!f || f.range_type !== "guest" || !(Number(f.guest_price) > 0)) { skipped.push(it.flavor_name || "guest product"); continue; }
      const avail = new Map((f.guest_options || []).map((o) => [o.label, Number(o.price) || 0]));
      const options = (it.options || []).filter((o) => avail.has(o.label)).map((o) => ({ label: o.label, price: avail.get(o.label) }));
      const unit = Number(f.guest_price) + options.reduce((a, o) => a + o.price, 0);
      items.push({ key: `guest:${f.id}:${options.map((o) => o.label).sort().join("|")}`, type: "guest", flavor_id: f.id, flavor_name: f.name, brand_name: f.brand_name || "",
        size_label: f.guest_size_label || "", guest_price: Number(f.guest_price), options, unit_price: Math.round(unit * 100) / 100, quantity: it.quantity, accent_color: f.accent_color, image_url: f.image_url });
    }
  }
  return { items, skipped };
};

export default function Reorder() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { clear, addItem } = useCart();
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    const q = token ? `?token=${encodeURIComponent(token)}` : "";
    Promise.all([api.get(`/orders/${id}${q}`), api.get("/flavors"), api.get("/sizes"), api.get("/multipacks")])
      .then(([o, f, s, m]) => {
        const { items, skipped } = rebuildOrderItems(o.data, { flavors: f.data, sizes: s.data, multipacks: m.data });
        if (!items.length) { setError("None of the items from this order are available any more."); return; }
        clear();
        items.forEach(addItem);
        toast.success(`Order ${o.data.order_number} is back in your basket at today's prices`);
        if (skipped.length) toast.info(`Not available any more: ${skipped.join(", ")}`);
        nav("/cart", { replace: true });
      })
      .catch(() => setError("We couldn't load that order. Check the link from your email or contact support."));
  }, [id, params, clear, addItem, nav]);

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-28 text-center" data-testid="reorder-page">
      {error ? (
        <>
          <h1 className="font-heading text-3xl font-bold" data-testid="reorder-error">{error}</h1>
          <Link to="/shop" className="mt-6 inline-block bg-foreground px-6 py-3 text-sm font-medium text-background">Browse the range</Link>
        </>
      ) : (
        <>
          <RotateCcw className="mx-auto h-8 w-8 text-primary" />
          <h1 className="mt-4 font-heading text-3xl font-bold">Rebuilding your order…</h1>
          <Loader2 className="mx-auto mt-6 h-5 w-5 animate-spin text-muted-foreground" />
        </>
      )}
    </div>
  );
}
